/**
 * #425 · El motivo con el que una persona confirma sacar de circulación un pedido que todavía
 * tiene planificaciones abiertas. El tope vive aquí, en un solo sitio, y lo usan los dos que lo
 * necesitan: el campo del aviso y la capa de datos que lo manda a la API.
 *
 * No es cosmética. La confirmación del BORRADO viaja en la URL —un DELETE no lleva cuerpo—, así
 * que un motivo largo se convierte en una URL que el navegador, el proxy o el propio servidor
 * pueden cortar con un 414: el pedido no se elimina y la persona no ve el motivo real del fallo
 * (revisión CODEX del PR #1783). Y por el otro camino, cancelar o desactivar, el mismo texto se
 * manda en el cuerpo y se queda sin tope dentro del histórico del pedido.
 */

/**
 * Tope en caracteres, el mismo que declara la API en `ConfirmacionSalidaPedidoDTO.MAX_MOTIVO`: si
 * se mueve uno, se mueve el otro. Es el que se le enseña a quien escribe («Quedan N de 300») y el
 * que lleva el `maxlength` del campo. Trescientos son tres o cuatro frases, que es lo que es un
 * motivo.
 */
export const LIMITE_MOTIVO_SALIDA = 300;

/**
 * Y el tope de verdad del camino del borrado: lo que puede ocupar el motivo <b>ya codificado</b>
 * dentro de la URL.
 *
 * Contar caracteres no basta y por eso hay dos topes (revisión CODEX del PR #1783, ronda 9):
 * `encodeURIComponent` gasta un número de caracteres distinto por cada uno, de 1 a 12, así que 300
 * caracteres pueden medir 300 o 2.700 según lo que se escriba. Trescientos acentos miden 1.800
 * —seis cada uno— y caben; trescientos kanji medirían 2.700 —nueve cada uno— y se llevarían por
 * delante la URL, que es el 414 que este módulo existe para evitar.
 *
 * 1.800 es lo que queda de los 2.000 que aguanta cualquier navegador, proxy o servidor después de
 * descontar la ruta del borrado con su `?confirmado=true&motivo=`, que mide unos 100. Está medido
 * en `scripts/prueba-aviso-planificaciones-abiertas.js` con los dos peores casos.
 */
export const LIMITE_MOTIVO_CODIFICADO = 1800;

/**
 * Lo que ocupa un carácter dentro de la URL, o `null` si no se puede meter en una.
 *
 * `encodeURIComponent` LANZA (`URIError`) con media pareja de un par sustituto suelta, que es lo
 * que deja un pegado roto o un recorte hecho a lo bruto. Un motivo con una de esas dentro no puede
 * tumbar un borrado: ese carácter no viaja y el resto sí.
 */
function costeEnLaUrl(caracter) {
    try {
        return encodeURIComponent(caracter).length;
    } catch (e) {
        return null;
    }
}

/**
 * El motivo tal y como se manda a la API: sin espacios de sobra, nunca más largo que el tope de
 * caracteres y nunca más gordo que lo que cabe en la URL del borrado.
 *
 * El campo ya lo limita al escribir, pero esto se aplica igual al salir: un motivo puesto por
 * código —otra pantalla, una prueba, un pegado que el `maxlength` no atrapara— no puede ser lo que
 * convierta un borrado en un 414.
 *
 * Y recorta por **caracteres completos**, recorriendo puntos de código en vez de cortar con un
 * `slice`: un corte en mitad de un par sustituto parte el carácter en dos, y esa mitad suelta hace
 * lanzar a `encodeURIComponent` justo al montar la URL.
 */
export function normalizarMotivoSalida(motivo) {
    const texto = (motivo === null || motivo === undefined ? '' : String(motivo)).trim();
    let recortado = '';
    let enLaUrl = 0;
    for (const caracter of texto) {
        if (recortado.length + caracter.length > LIMITE_MOTIVO_SALIDA) break;
        const coste = costeEnLaUrl(caracter);
        if (coste === null) continue;
        if (enLaUrl + coste > LIMITE_MOTIVO_CODIFICADO) break;
        recortado += caracter;
        enLaUrl += coste;
    }
    return recortado;
}
